import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import GoToDashboardButton from '@sputnik/components/GoToDashboardButton'
import TilaErrorMessage from '@sputnik/components/TilaErrorMessage'
import useGetInfoForApplicationAgreements from '@sputnik/hooks/useGetInfoForApplicationAgreements'
import { useUpdateCreditLineOfferSelectedDecision } from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/UpdateCreditLineOfferSelectedDecision.gql'
import { GetEligibleOffers_decisions_Decision } from '@sputnik/pages/PostApplicationPages/EligibleOffersPage/GetEligibleOffers.gql'
import OfferTermsCheckbox from '@sputnik/pages/PostApplicationPages/EligibleOffersPage/OfferTermsCheckbox'
import styles from '@sputnik/pages/PostApplicationPages/EligibleOffersPage/styles.module.scss'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

export interface EligibleOffersPageFooterProps {
  selectedOffer: GetEligibleOffers_decisions_Decision | null | undefined
}

const EligibleOffersPageFooter = ({ selectedOffer }: EligibleOffersPageFooterProps) => {
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false)

  const handleCheckboxClick = () => {
    setHasAgreedToTerms(!hasAgreedToTerms)
  }

  const [getClassName, makeTestId] = useNameFormatter('EligibleOffersPageFooter')
  const { appId } = useParams()
  const navigate = useNavigate()
  const showToast = useToast()

  const [updateCreditLineOfferSelectedDecision, { loading: isLoadingAcceptingOffer }] =
    useUpdateCreditLineOfferSelectedDecision({
      onCompleted: () => {
        logInfo('updateCreditLineOfferSelectedDecision', {
          attributes: {
            creditAppId: appId,
            result: `accepted offer for ${selectedOffer?.offerType} and sending to polling (/app/${appId}/${PATHNAME_CONSTS.ACCEPTING_OFFER})`,
          },
          eventName: 'CreditLineOfferPageSuccess',
        })

        navigate({ pathname: `/app/${appId}/${PATHNAME_CONSTS.ACCEPTING_OFFER}` })
      },
      onError: (e) => {
        logError(e, {
          attributes: {
            action: 'updateCreditLineOfferSelectedDecision',
            creditAppId: appId,
            message: e?.message,
            result: `Error accepting offer for ${selectedOffer?.offerType}`,
          },
          eventName: 'CreditLineOfferPageFailure',
        })

        showToast(
          TOAST_TYPE_DANGER,
          <FormattedMessage
            defaultMessage='There was an error accepting your offer.'
            id='sputnik.EligibleOffersPageFooter__oRIVFo'
          />,
          {
            className: 'fs-unmask',
            autoHideDelay: 5000,
          },
        )
      },
    })

  const {
    isLoggedInUserAuthorizedSigner,
    hasAgreedToTila,
    authEmail,
    isLoading,
    disableAcceptOfferButton,
    tilaInfoError,
    hasTilaContent,
    creditApplication,
  } = useGetInfoForApplicationAgreements({
    decision: selectedOffer,
    isLoadingAcceptingOffer,
    hasAgreedToTerms,
  })

  const canViewCheckbox = hasTilaContent ? hasAgreedToTila : !!selectedOffer

  const handleAcceptClick = () => {
    if (!selectedOffer?.id) {
      return
    }

    updateCreditLineOfferSelectedDecision({
      variables: {
        id: selectedOffer?.id,
        salesforceCreditId: creditApplication?.salesforceCreditId,
      },
    })
  }

  return (
    <div
      className={styles[getClassName('')]}
      data-testid={makeTestId('')}
    >
      {tilaInfoError && <TilaErrorMessage />}
      {!isLoggedInUserAuthorizedSigner && !tilaInfoError && (
        <div className={styles[getClassName('unauthorized-accept-message')]}>
          <FormattedMessage
            defaultMessage='Only the authorized signer ({email}) can accept this offer.'
            id='sputnik.EligibleOffersPageFooter__TdYnmo'
            values={{
              email: authEmail,
            }}
          />
        </div>
      )}
      {canViewCheckbox && (
        <OfferTermsCheckbox
          creditApplication={creditApplication}
          hasAgreedToTerms={hasAgreedToTerms}
          onCheckboxClick={handleCheckboxClick}
        />
      )}
      <div className={styles[getClassName('footer')]}>
        <GoToDashboardButton
          buttonType={BASIC_BUTTON_TYPE_OUTLINED}
          componentName={getClassName('')}
        />
        <div>
          {isLoggedInUserAuthorizedSigner && (
            <BasicButton
              disabled={isLoading || disableAcceptOfferButton}
              showSpinner={isLoadingAcceptingOffer}
              onClick={handleAcceptClick}
            >
              <FormattedMessage
                defaultMessage='Accept offer'
                id='sputnik.EligibleOffersPageFooter__/xro5W'
              />
            </BasicButton>
          )}
        </div>
      </div>
    </div>
  )
}

export default EligibleOffersPageFooter
